import { Response } from '../Response';
import { Endpoint } from '../Endpoint';
import { HttpClient } from '@angular/common/http';
import { IListResponse } from '../IListResponse';
import { Injectable, Inject } from '@angular/core';
import { API_SERVICE_CONFIG } from '../ApiServiceConfig';
import { IApiServiceConfig } from '../IApiServiceConfig';

import { Observable, BehaviorSubject } from 'rxjs';

import { TicketSupportMultimachine } from '../../models/v0/TicketSupportMultimachine';
import { UnsavedTicketSupportMultimachine } from '../../models/v0/UnsavedTicketSupportMultimachine';
import { ISerialisedTicketSupportMultimachine } from '../../models/v0/serialisations/ISerialisedTicketSupportMultimachine';
import { map, tap } from 'rxjs/operators';

type TicketSupportMultimachineList =
  IListResponse<ISerialisedTicketSupportMultimachine>;

@Injectable()
export class TicketsSupportMultimachineEndpoint extends Endpoint<
  TicketSupportMultimachine,
  ISerialisedTicketSupportMultimachine
> {
  private readonly url = this.config.url + 'tickets/multimachinesupports/';

  constructor(
    @Inject(API_SERVICE_CONFIG) private config: IApiServiceConfig,
    private http: HttpClient,
  ) {
    super(TicketSupportMultimachine.fromSerialised);
  }

  getAll(
    filters: {},
    source?: Observable<TicketSupportMultimachineList>,
    store?: BehaviorSubject<Response<TicketSupportMultimachine[]>>,
  ): Observable<Response<TicketSupportMultimachine[]>> {
    let query = '';
    Object.keys(filters).forEach((key) => (query += `&${key}=${filters[key]}`));

    const defaultSource = this.http.get<TicketSupportMultimachineList>(
      `${this.url}?size=2000&page=0${query}`,
    );

    return this.list(source || defaultSource, store);
  }

  get(
    id: string,
    source?: Observable<ISerialisedTicketSupportMultimachine>,
    store?: BehaviorSubject<Response<TicketSupportMultimachine[]>>,
  ): Observable<Response<TicketSupportMultimachine>> {
    const defaultSource = this.http.get<ISerialisedTicketSupportMultimachine>(
      `${this.url}${id}/`,
    );
    return this.retrieve(source || defaultSource, store);
  }

  post(
    ticketSupport: UnsavedTicketSupportMultimachine,
    source?: Observable<ISerialisedTicketSupportMultimachine>,
    store?: BehaviorSubject<Response<TicketSupportMultimachine[]>>,
  ): Observable<Response<TicketSupportMultimachine>> {
    const { ...body } = ticketSupport.toSerialised();
    const defaultSource = this.http.post<ISerialisedTicketSupportMultimachine>(
      this.url,
      body,
    );
    return this.add(source || defaultSource, store);
  }

  patch(
    id: string,
    ticketSupport: UnsavedTicketSupportMultimachine,
    source?: Observable<ISerialisedTicketSupportMultimachine>,
    store?: BehaviorSubject<Response<TicketSupportMultimachine[]>>,
  ) {
    const { ...body } = ticketSupport.toSerialised();
    const defaultSource = this.http.patch<ISerialisedTicketSupportMultimachine>(
      `${this.url}${id}/`,
      body,
    );

    return defaultSource.pipe(
      tap((instance) =>
        this.update(
          source || defaultSource,
          TicketSupportMultimachine.fromSerialised(instance),
          store,
        ),
      ),
      map(
        (serialiser) =>
          new Response(TicketSupportMultimachine.fromSerialised(serialiser)),
      ),
    );
  }

  delete(
    id: string,
    source?: Observable<{}>,
    store?: BehaviorSubject<Response<TicketSupportMultimachine[]>>,
  ): Observable<Response<{}>> {
    const defaultSource = this.http.delete<{}>(`${this.url}${id}/`);
    return this.remove(source || defaultSource, id, store);
  }
}
